import { debounce } from '@theme/utils'

class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super()
    this.sectionId = this.dataset.sectionId
    this.context = this.dataset.context || ''
    this.limit = Number(this.dataset.limit) || 6
    this.types = (this.dataset.types || 'product')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
    this.unavailableProducts = this.dataset.unavailableProducts || ''
    this.input = this.querySelector('input[type="search"]')
    this.resultsContainer = this.querySelector(
      `#PredictiveSearchResults-${this.sectionId}`
    )
    this.clearButton = this.querySelector('.clear')
    this.controller = null
    this.debouncedFetch = debounce(this.fetchResults.bind(this), 200)
  }

  connectedCallback() {
    if (!(this.input && this.resultsContainer)) return
    this.input.addEventListener('input', this.handleInput.bind(this))
    this.addEventListener('reset', this.handleReset.bind(this))
    if (this.context === 'drawer' || this.closest('.search-drawer')) {
      this.addEventListener('submit', this.handleSubmit.bind(this))
    }
  }

  handleInput(event) {
    const query = event.target.value.trim()
    this.toggleClearButton(query.length > 0)
    if (!query) {
      this.debouncedFetch.cancel()
      this.clearResults()
      return
    }
    this.debouncedFetch(query)
  }

  handleReset() {
    this.toggleClearButton(false)
    this.clearResults()
    if (this.input) this.input.focus()
  }

  handleSubmit(event) {
    if (this.context !== 'drawer' && !this.closest('.search-drawer')) return
    event.preventDefault()
    const query = this.input ? this.input.value.trim() : ''
    if (!query) {
      this.clearResults()
      return
    }
    this.debouncedFetch.cancel()
    this.fetchResults(query)
  }

  toggleClearButton(visible) {
    if (!this.clearButton) return
    this.clearButton.style.opacity = visible ? '1' : '0'
    this.clearButton.setAttribute('aria-hidden', visible ? 'false' : 'true')
    this.clearButton.tabIndex = visible ? 0 : -1
  }

  async fetchResults(query) {
    if (this.controller) this.controller.abort()
    this.controller = new AbortController()

    const root = window.Shopify?.routes?.root || '/'
    const params = new URLSearchParams({
      q: query,
      'resources[limit_scope]': 'each',
      'resources[limit]': String(this.limit),
      'resources[type]': this.types.join(','),
      section_id: this.sectionId
    })
    if (this.unavailableProducts) {
      params.set(
        'resources[options][unavailable_products]',
        this.unavailableProducts
      )
    }

    try {
      const response = await fetch(`${root}search/suggest?${params}`, {
        signal: this.controller.signal
      })
      if (!response.ok)
        throw new Error(`Predictive search failed: ${response.status}`)

      const text = await response.text()
      const doc = new DOMParser().parseFromString(text, 'text/html')
      const newResults = doc.querySelector(
        `#PredictiveSearchResults-${this.sectionId}`
      )
      if (newResults) {
        this.resultsContainer.innerHTML = newResults.innerHTML
      }
    } catch (err) {
      if (err.name === 'AbortError') return
    }
  }

  clearResults() {
    if (this.resultsContainer) this.resultsContainer.innerHTML = ''
  }
}

if (!customElements.get('ui-predictive-search')) {
  customElements.define('ui-predictive-search', PredictiveSearch, {
    extends: 'form'
  })
}
